import { ReactNode, useContext } from 'react';
import { useHistory } from '@alltrails/shared/components/ReactRouter';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import Button from '@alltrails/shared/denali/components/Button';
import useWindowWidth from 'hooks/useWindowWidth';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import * as styles from './GiftReview.module.scss';
import GiftCard from '../GiftCard';
import { GiftFlowContext, RESTART_GIFT_FLOW } from '../GiftFlow/GiftFlow';
import { monthDayYear } from '../../../utils/time_helpers';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { pushHistory } from '../../../utils/client_side_routing_helpers';
import ShopifyPreview from '../../blocks/ShopifyPreview';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';

const GiftCardAndMessage = ({ message, background }: { message: ReactNode; background: string }) => (
  <div>
    <GiftCard backgroundId={background} classes={styles.giftCard} />
    <div className={styles.giftMessage}>{message}</div>
  </div>
);

const Info = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <div className={styles.infoContainer}>
    <span>{label}</span>: <span className={styles.value}>{value}</span>
  </div>
);

type Props = {
  background: string;
  code: string;
  message?: unknown;
  metadata: {
    deliveryDate: any;
  };
  recipientEmail: string;
  recipientName: string;
  isEmail?: boolean;
  showShopifyPreview?: unknown;
  shopifyProducts: {
    images: {
      src: string;
      alt: string;
    }[];
    title: string;
    shopItemUrl: string;
    price: string;
  }[];
};

const GiftReview = ({ background, message, metadata, recipientEmail, recipientName, isEmail, code, shopifyProducts, showShopifyPreview }: Props) => {
  const languageRegionCode = useLanguageRegionCode();
  const windowWidth = useWindowWidth({});

  const { dispatchGiftFlow } = useContext(GiftFlowContext);
  const history = useHistory();

  const buyAgain = () => {
    dispatchGiftFlow({ type: RESTART_GIFT_FLOW });
    pushHistory('/gift', history, languageRegionCode);
  };

  const startExploring = () => {
    window.location.href = LanguageSupportUtil.wrapUrlSafe('/explore', languageRegionCode);
  };

  const downloadGiftCard = () => {
    pushHistory(`/gift/preview?${code}`, history, languageRegionCode);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardColumn}>
          <GiftCardAndMessage background={background} message={message} />
        </div>
        <div className={styles.metadataColumn}>
          <div className={styles.header}>
            <div>
              <FormattedMessage defaultMessage="Gift subscription purchased." />
            </div>
            <div>
              <FormattedMessage defaultMessage="Thank you!" />
            </div>
            {windowWidth && windowWidth < DESKTOP_BREAKPOINT && <GiftCardAndMessage background={background} message={message} />}
          </div>
          <div className={styles.metadata}>
            <div className={isEmail ? '' : styles.printStyles}>
              <Info label={<FormattedMessage defaultMessage="To" />} value={recipientName} />
              {isEmail && <Info label={<FormattedMessage defaultMessage="Email" />} value={recipientEmail} />}
              <Info label={<FormattedMessage defaultMessage="Plan" />} value={<FormattedMessage defaultMessage="Annual Plan" />} />
              {isEmail && (
                <Info label={<FormattedMessage defaultMessage="Send date" />} value={monthDayYear(metadata.deliveryDate, languageRegionCode)} />
              )}
              {isEmail ? (
                <>
                  <div className={styles.spacer}>
                    <FormattedMessage defaultMessage="Thank you for giving the gift of AllTrails Pro. We are in the process of delivering the gift subscription to the recipient. We will inform you when it has been sent." />
                  </div>
                  <div className={styles.spacer}>
                    <FormattedMessage defaultMessage="Check your inbox for a confirmation email." />
                  </div>
                </>
              ) : (
                <div className={styles.spacer}>
                  <FormattedMessage defaultMessage="Check your inbox for a confirmation email. Download the gift subscription below." />
                </div>
              )}
              {!isEmail && (
                <Button
                  text={<FormattedMessage defaultMessage="Download gift subscription" />}
                  className={styles.spacer}
                  onClick={downloadGiftCard}
                  testId="gift-review-download-gift-subscription"
                  variant="default"
                />
              )}
            </div>
            <Button
              text={<FormattedMessage defaultMessage="Buy again" />}
              className={styles.spacer}
              onClick={buyAgain}
              testId="gift-review-buy-again"
              variant="primary"
            />
            <Button
              text={<FormattedMessage defaultMessage="Start exploring!" />}
              className={styles.spacer}
              onClick={startExploring}
              testId="gift-review-start-exploring"
              variant="default"
            />
          </div>
        </div>
      </div>
      {showShopifyPreview && <ShopifyPreview shopifyProducts={shopifyProducts} windowWidth={windowWidth} />}
    </>
  );
};

GiftReview.defaultProps = {
  isEmail: false,
  message: '',
  showShopifyPreview: false,
  shopifyProducts: []
};

export default GiftReview;
